import React, { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/Button';
import { Link, Navigate } from 'react-router-dom';
import { getStats } from '../services/api';
import { Award, BarChart3, Bookmark, BookOpen, PenTool } from 'lucide-react';

export const Profile: React.FC = () => {
  const { user, logout } = useAuth();
  const [stats, setStats] = useState<any | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      if (!user) return;
      try {
        const res = await getStats();
        setStats(res);
      } catch {
        setStats(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [user]);

  if (!user) return <Navigate to="/" />;

  const totalMinutes = stats?.totalMinutes || 0;
  const streak = stats?.streak || user.streak || 0;
  const badges = stats?.badges || [];
  const completed = user.booksRead || 0;

  return (
    <div className="pb-24 px-6 pt-8 max-w-3xl mx-auto animate-in slide-in-from-bottom-4 duration-500">
      {/* Identity */}
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center gap-4">
          <div className="w-12 h-12 rounded-full bg-brand-blue text-white flex items-center justify-center font-serif text-lg">
            {(user.name || user.email)[0]?.toUpperCase()}
          </div>
          <div>
            <p className="text-xs uppercase tracking-wider opacity-50">Identity</p>
            <h1 className="text-2xl font-serif font-bold">{user.name || '—'}</h1>
            <p className="text-sm opacity-60">{user.email}</p>
            <p className="text-sm opacity-70 mt-2">
              This is how you appear in the lab.<br />Real name optional. Work optional. Curiosity not.
            </p>
          </div>
        </div>
        <Button variant="outline" onClick={logout} className="text-red-500 border-red-200 hover:bg-red-50 dark:border-red-900 dark:hover:bg-red-900/20">
          Log Out
        </Button>
      </div>

      {/* Metrics */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <MetricCard
          label="Reading Time"
          value={`${Math.floor(totalMinutes / 60)}h ${totalMinutes % 60}m`}
          sub="No time recorded in the files yet."
          icon={<BarChart3 size={16} />}
        />
        <MetricCard
          label="Current Streak"
          value={`${streak} days`}
          sub="No reading streak. Easy to fix, easy to ignore."
          icon={<Bookmark size={16} />}
        />
        <MetricCard
          label="Case Files Completed"
          value={`${completed}`}
          sub="You haven’t finished any case files here yet."
          icon={<BookOpen size={16} />}
        />
      </div>

      {/* Author CTA */}
      <section className="mb-8">
        <div className="p-5 rounded-2xl border border-brand-blue/30 bg-brand-blue/5 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
          <div>
            <p className="text-xs uppercase tracking-wider font-bold text-brand-blue mb-1">For Authors</p>
            <h3 className="font-serif text-xl font-bold mb-2">Publish Your Next Case</h3>
            <p className="text-sm opacity-70 max-w-md">
              Upload chapters, set which parts are open to everyone, and release your work into the shared space.
            </p>
          </div>
          <Link to="/add-book">
            <Button className="gap-2">
              <PenTool size={16} />
              Publish
            </Button>
          </Link>
        </div>
      </section>

      {/* Badges / Milestones */}
      <section className="mb-8">
        <h2 className="font-bold mb-3 flex items-center gap-2"><Award size={16} /> Milestones</h2>
        <div className="grid grid-cols-2 gap-3">
          <Milestone title="Bookworm" desc="Read 10 hours of case files." earned={badges.find((b: any) => b.id === 'bookworm')?.earned} />
          <Milestone title="3-Day Streak" desc="Read three days in a row." earned={badges.find((b: any) => b.id === 'streak-3')?.earned} />
          <Milestone title="7-Day Streak" desc="Stay with it for a week." earned={badges.find((b: any) => b.id === 'streak-7')?.earned} />
        </div>
      </section>

      {/* Quick links */}
      <section className="mb-6">
        <h2 className="font-bold mb-3">Quick Links</h2>
        <div className="grid grid-cols-2 gap-3">
          <LinkCard to="/library" title="My Archive" desc="all your saved cases" />
          <LinkCard to="/bookmarks" title="Bookmarks" desc="lines you flagged" />
          <LinkCard to="/stats" title="Stats" desc="how you actually read" />
          <LinkCard to="/add-book" title="Publish" desc="add something new" />
        </div>
      </section>
    </div>
  );
};

const MetricCard: React.FC<{ label: string; value: string; sub: string; icon: React.ReactNode }> = ({ label, value, sub, icon }) => (
  <div className="p-4 rounded-2xl border border-black/5 dark:border-white/5 shadow-sm flex flex-col gap-2 bg-white/60 dark:bg-white/5">
    <div className="flex items-center gap-2">
      <div className="p-2 rounded-lg bg-brand-blue/10 text-brand-blue">{icon}</div>
      <div>
        <p className="text-[10px] uppercase tracking-wider opacity-50 font-bold">{label}</p>
        <p className="text-lg font-bold">{value}</p>
      </div>
    </div>
    <p className="text-xs opacity-60 leading-snug">{sub}</p>
  </div>
);

const LinkCard: React.FC<{ to: string; title: string; desc: string }> = ({ to, title, desc }) => (
  <Link
    to={to}
    className="p-4 rounded-xl border border-black/5 dark:border-white/10 hover:border-brand-blue/60 transition-all shadow-sm bg-black/5 dark:bg-white/5 hover:-translate-y-0.5"
  >
    <h4 className="font-bold text-sm mb-1">{title}</h4>
    <p className="text-xs opacity-60">{desc}</p>
  </Link>
);

const Milestone: React.FC<{ title: string; desc: string; earned?: boolean }> = ({ title, desc, earned }) => (
  <div
    className={`p-4 rounded-xl border flex items-start gap-3 ${
      earned ? 'border-brand-blue bg-brand-blue/5' : 'border-black/5 dark:border-white/5 opacity-70'
    }`}
  >
    <div className="p-2 rounded-lg bg-black/5 dark:bg-white/10">
      <Award size={18} />
    </div>
    <div>
      <h4 className="font-bold text-sm leading-tight">{title}</h4>
      <p className="text-xs opacity-60 leading-tight">{desc}</p>
    </div>
  </div>
);
